import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listTemplateProposals,
  reviewTemplateProposal,
} from "@/lib/template-proposals.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/proposals")({
  component: AdminProposalsPage,
});

type StatusFilter = "pending_review" | "approved" | "rejected" | "all";

function AdminProposalsPage() {
  const list = useServerFn(listTemplateProposals);
  const review = useServerFn(reviewTemplateProposal);
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("pending_review");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-template-proposals", status],
    queryFn: () =>
      list({
        data: {
          status: status === "all" ? null : status,
          limit: 100,
        },
      }),
  });

  const mut = useMutation({
    mutationFn: (input: {
      proposal_id: string;
      decision: "approved" | "rejected";
    }) => review({ data: input }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["admin-template-proposals"] });
      toast.success(
        res?.applied ? `Approved, ${res.applied}` : "Proposal updated",
      );
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const proposals = data?.proposals ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground ml-auto self-center max-w-md">
          Proposals fire when ≥2 distinct workspaces report the same signal on a
          shared program template.
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading proposals…</p>
      )}
      {!isLoading && proposals.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No proposals for this filter.
        </div>
      )}

      <div className="space-y-2">
        {proposals.map((p: any) => {
          const change = (p.proposed_change_json ?? {}) as any;
          const statusBadge =
            p.status === "approved"
              ? { text: "Approved", variant: "default" as const }
              : p.status === "rejected"
                ? { text: "Rejected", variant: "destructive" as const }
                : { text: "Pending review", variant: "secondary" as const };
          return (
            <div key={p.id} className="rounded-md border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{p.template_name}</Badge>
                <Badge variant="outline">{change.type ?? "unknown"}</Badge>
                <Badge variant={statusBadge.variant}>{statusBadge.text}</Badge>
                <span className="text-xs text-muted-foreground">
                  {change.contributor_workspace_count ?? "?"} workspace
                  {change.contributor_workspace_count === 1 ? "" : "s"} ·{" "}
                  {change.agreement_count ?? "?"} agree · created{" "}
                  {timeAgo(p.created_at)}
                </span>
              </div>
              <div className="mt-2 text-sm font-medium">
                {change.signal_key ?? "(no key)"}
              </div>
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted/40 p-2 text-xs">
                {JSON.stringify(change.value ?? null, null, 2)}
              </pre>
              {p.status === "pending_review" && (
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      mut.mutate({ proposal_id: p.id, decision: "approved" })
                    }
                    disabled={mut.isPending}
                  >
                    Approve & apply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      mut.mutate({ proposal_id: p.id, decision: "rejected" })
                    }
                    disabled={mut.isPending}
                  >
                    Reject
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return ",";
  const diff = Date.now() - new Date(iso).getTime();
  if (!isFinite(diff) || diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
